import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import { 
  useAssignJob, 
  useReassignJob, 
  useReleaseJobAssignment,
  useListAllUsers,
  type JobResponse,
  type OpsJobItem,
  type BadRequestResponse,
  type UnauthorizedResponse,
  type ForbiddenResponse,
  type NotFoundResponse,
  type ErrorResponse,
  type InternalServerErrorResponse
} from '@taxassist/api-client';
import { UserDisplay } from '../common/UserDisplay';

// Union type for all possible API errors
type MutationError = 
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ErrorResponse
  | InternalServerErrorResponse;

interface JobAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  job: JobResponse | OpsJobItem | null;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export const JobAssignmentDialog: React.FC<JobAssignmentDialogProps> = ({
  open,
  onClose,
  job,
  onSuccess,
  onError
}) => {
  const [selectedReviewer, setSelectedReviewer] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [isReleasing, setIsReleasing] = useState<boolean>(false);
  const [reasonTouched, setReasonTouched] = useState<boolean>(false);

  // Fetch available reviewers
  const { data: reviewersData, isLoading: reviewersLoading } = useListAllUsers(
    open ? { roles: ['admin', 'reviewer'], limit: 500 } : undefined
  );

  // Assignment mutations
  const assignJobMutation = useAssignJob({
    mutation: {
      onSuccess: () => {
        onSuccess?.();
        handleClose();
      },
      onError: (error: MutationError) => {
        onError?.(error.message || 'Failed to assign job');
      },
    }
  });

  const reassignJobMutation = useReassignJob({
    mutation: {
      onSuccess: () => {
        onSuccess?.();
        handleClose();
      },
      onError: (error: MutationError) => {
        onError?.(error.message || 'Failed to reassign job');
      },
    }
  });

  const releaseJobMutation = useReleaseJobAssignment({
    mutation: {
      onSuccess: () => {
        onSuccess?.();
        handleClose();
      },
      onError: (error: MutationError) => {
        onError?.(error.message || 'Failed to release assignment');
      },
    }
  });

  const handleClose = () => {
    setSelectedReviewer('');
    setReason('');
    setIsReleasing(false);
    setReasonTouched(false);
    onClose();
  };

  const handleAssign = async () => {
    if (!job) return;

    if (reasonRequired && !reason.trim()) {
      setReasonTouched(true);
      return;
    }

    if (isReleasing) {
      // Release assignment
      await releaseJobMutation.mutateAsync({ 
        jobId: job.jobId,
        params: reason ? { reason } : undefined
      });
    } else if (selectedReviewer) {
      // Assign or reassign job
      const assignmentData = {
        reviewerId: selectedReviewer,
        ...(reason && { reason })
      };

      if (job.assignedReviewer) {
        // Job is already assigned, so reassign
        await reassignJobMutation.mutateAsync({
          jobId: job.jobId,
          data: assignmentData
        });
      } else {
        // Job is not assigned, so assign
        await assignJobMutation.mutateAsync({
          jobId: job.jobId,
          data: assignmentData
        });
      }
    }
  };

  const handleReleaseToggle = () => {
    setIsReleasing(!isReleasing);
    setSelectedReviewer('');
  };

  const isLoading = assignJobMutation.isPending || reassignJobMutation.isPending || releaseJobMutation.isPending;
  const isReassigning = !isReleasing && !!job?.assignedReviewer;
  const reasonRequired = isReassigning;
  const canSubmit = isReleasing || (selectedReviewer && (!reasonRequired || reason.trim()));

  if (!job) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon />
          {isReleasing ? 'Release Assignment' : 'Assign Job'}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Job Info */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Job Details
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {job.clientName} - {job.filingType} {job.taxYear}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Currently assigned to:
              </Typography>
              <UserDisplay 
                userId={job.assignedReviewer}
                variant="body2"
                color="textPrimary"
                contextFirmId={job.firmId}
                fallback="Unassigned"
              />
            </Box>
          </Box>

          {/* Assignment Options */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant={!isReleasing ? "contained" : "outlined"}
              onClick={() => setIsReleasing(false)}
              startIcon={<PersonAddIcon />}
              size="small"
            >
              {job.assignedReviewer ? 'Reassign' : 'Assign'}
            </Button>
            {job.assignedReviewer && (
              <Button
                variant={isReleasing ? "contained" : "outlined"}
                onClick={handleReleaseToggle}
                startIcon={<PersonOffIcon />}
                size="small"
                color="warning"
              >
                Release
              </Button>
            )}
          </Box>

          {/* Reviewer Selection */}
          {!isReleasing && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Reviewer</InputLabel>
              <Select
                value={selectedReviewer}
                onChange={(e) => setSelectedReviewer(e.target.value)}
                label="Select Reviewer"
                disabled={reviewersLoading}
              >
                {reviewersData?.users
                  ?.filter(reviewer => reviewer.userId !== job.assignedReviewer) // Don't show current assignee
                  ?.map((reviewer) => (
                    <MenuItem key={reviewer.userId} value={reviewer.userId}>
                      {reviewer.firstName} {reviewer.lastName} ({reviewer.role})
                    </MenuItem>
                  ))}
              </Select>
              {reviewersLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </FormControl>
          )}

          {/* Reason Field */}
          <TextField
            fullWidth
            label={
              isReleasing
                ? "Reason for Release (Optional)"
                : reasonRequired
                ? "Reason for Reassignment (Required)"
                : "Reason for Assignment (Optional)"
            }
            required={reasonRequired}
            error={reasonRequired && !reason.trim() && reasonTouched}
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => { if (reasonRequired) setReasonTouched(true); }}
            placeholder={
              isReleasing
                ? "Why are you releasing this assignment?"
                : "Why are you assigning/reassigning this job?"
            }
            helperText={
              reasonRequired && !reason.trim() && reasonTouched
                ? "A reason is required for reassignment"
                : `${reason.length}/500 characters`
            }
            inputProps={{ maxLength: 500 }}
            sx={{ mb: 2 }}
          />

          {/* Warning for reassignment */}
          {!isReleasing && job.assignedReviewer && selectedReviewer && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This job will be reassigned from the current reviewer to the selected reviewer.
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!canSubmit || isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} />
            ) : isReleasing ? (
              <PersonOffIcon />
            ) : (
              <PersonAddIcon />
            )
          }
          color={isReleasing ? "warning" : "primary"}
        >
          {isLoading
            ? 'Processing...'
            : isReleasing
            ? 'Release Assignment'
            : job.assignedReviewer
            ? 'Reassign Job'
            : 'Assign Job'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};